import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { getCart, addCartItem, updateCartItem, removeCartItem } from "../api/cartService";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { isAuthenticated, openLoginModal } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!isAuthenticated) {
      setItems([]);
      return Promise.resolve();
    }
    setLoading(true);
    return getCart()
      .then((cart) => setItems(cart.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  async function addItem(productId, { variantId, quantity = 1, isWholesale = false } = {}) {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    const cart = await addCartItem({ productId, variantId, quantity, isWholesale });
    setItems(cart.items || []);
  }

  async function updateItem(cartItemId, quantity) {
    const cart = await updateCartItem(cartItemId, quantity);
    setItems(cart.items || []);
  }

  async function removeItem(cartItemId) {
    const cart = await removeCartItem(cartItemId);
    setItems(cart.items || []);
  }

  const value = { items, itemCount, loading, addItem, updateItem, removeItem, refresh };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
